/**
 * PostMealCompleteSheet Component
 * 
 * P0: Post-meal completion flow - No more stranded users
 * Shows immediately after ANY successful meal log + totals update.
 * 
 * Features:
 * - Dynamic calorie/protein display
 * - Next-best-action from useNextBestStep.js
 * - Secondary actions (Smart Picks, Barcode) when applicable
 * - 2-hour cooldown to prevent spam
 * - Evening snack suggestion when 100-500 kcal remaining
 */

import React from 'react';

// Evening snack suggestions (same as useNextBestStep)
const EVENING_SNACK_SUGGESTIONS = [
  { name: 'Apple + 1 tbsp peanut butter', calories: '~190 kcal' },
  { name: 'Greek yogurt with berries', calories: '~150 kcal' },
  { name: 'Handful of almonds (15-20)', calories: '~160 kcal' },
  { name: 'Cottage cheese with fruit', calories: '~180 kcal' },
  { name: 'String cheese + crackers', calories: '~170 kcal' },
];

// Check if we're in evening window (6-9pm)
const isEveningWindow = (forceEvening = false) => {
  if (forceEvening) return true;
  const hour = new Date().getHours();
  return hour >= 18 && hour < 21;
};

// Check 2-hour cooldown
const COOLDOWN_KEY = 'post_meal_sheet_cooldown';
const COOLDOWN_MS = 2 * 60 * 60 * 1000; // 2 hours

export const shouldShowPostMealSheet = () => {
  const lastShown = localStorage.getItem(COOLDOWN_KEY);
  if (!lastShown) return true;
  return Date.now() - parseInt(lastShown, 10) > COOLDOWN_MS;
};

export const markPostMealSheetShown = () => {
  localStorage.setItem(COOLDOWN_KEY, Date.now().toString());
};

const PostMealCompleteSheet = ({
  isOpen,
  onClose,
  // Current meal data (what was just logged)
  currentMealName = null,
  currentMealCalories = 0,
  currentMealProtein = 0,
  currentMealCarbs = 0,
  currentMealFat = 0,
  // Medical sensitivity alerts
  medicalAlerts = [],
  // Nutrition data (cumulative)
  todayCalories = 0,
  calorieTarget = 0,
  todayProtein = 0,
  todayCarbs = 0,
  todayFat = 0,
  mealsLoggedToday = 0,
  hasAIGoals = false,
  hasCalorieTarget = false,
  // Micronutrients (optional - show toggle if exists or Pro user)
  micronutrients = null,
  isPro = false,
  // Premium insights gating
  isTrial = false,
  onUpgrade,
  // Next best action from useNextBestStep
  nextBestAction = null,
  // Handlers
  onPrimaryAction,
  onSmartSwaps,
  onBarcodeScanner,
  // Feature flags
  showSmartSwaps = false,
  showBarcode = false,
  // Test mode props (for verification scenarios)
  _testForceEvening = false,  // Force evening mode for testing
  _testForceNoProfile = false,  // Force profile incomplete for testing
}) => {
  const [showMicros, setShowMicros] = React.useState(false);
  const [showAllAlerts, setShowAllAlerts] = React.useState(false);
  
  if (!isOpen) return null;

  const remainingCalories = calorieTarget - todayCalories;
  const isEvening = isEveningWindow(_testForceEvening);
  const hasEveningGap = isEvening && remainingCalories >= 100 && remainingCalories <= 500;
  
  // Get random evening snack suggestion
  const eveningSnack = hasEveningGap 
    ? EVENING_SNACK_SUGGESTIONS[Math.floor(Math.random() * EVENING_SNACK_SUGGESTIONS.length)]
    : null;

  // Determine primary CTA
  const getPrimaryCTA = () => {
    // Edge case: Profile incomplete (test mode or from nextBestAction)
    if (_testForceNoProfile || (nextBestAction?.primaryAction === 'COMPLETE_PROFILE')) {
      return {
        label: '🧍 Complete Profile',
        action: 'COMPLETE_PROFILE'
      };
    }
    
    // Edge case: No calorie target → force SmartGoals
    if (!hasCalorieTarget) {
      return {
        label: '🎯 Activate SmartGoals',
        action: 'SET_GOALS_TIMELINE'
      };
    }
    
    // Use next best action from hook
    if (nextBestAction) {
      return {
        label: nextBestAction.primaryLabel,
        action: nextBestAction.primaryAction
      };
    }
    
    // Fallback
    return {
      label: '📸 Log Another Meal',
      action: 'TAKE_PHOTO'
    };
  };

  const primaryCTA = getPrimaryCTA();

  const handlePrimaryClick = () => {
    markPostMealSheetShown();
    if (onPrimaryAction) onPrimaryAction(primaryCTA.action);
    onClose();
  };

  const handleSmartSwaps = () => {
    markPostMealSheetShown();
    if (onSmartSwaps) onSmartSwaps();
    onClose();
  };

  const handleBarcode = () => {
    markPostMealSheetShown();
    if (onBarcodeScanner) onBarcodeScanner();
    onClose();
  };

  const handleNotNow = () => {
    markPostMealSheetShown();
    onClose();
  };

  return (
    <>
      {/* Backdrop */}
      <div 
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.6)',
          backdropFilter: 'blur(4px)',
          zIndex: 999,
          animation: 'fadeIn 0.2s ease-out'
        }}
        onClick={handleNotNow}
      />
      
      {/* Bottom Sheet */}
      <div 
        data-testid="post-meal-complete-sheet"
        style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          background: 'var(--card-bg, #1a1a2e)',
          borderRadius: '20px 20px 0 0',
          padding: '24px 20px 32px',
          zIndex: 1000,
          animation: 'slideUp 0.3s ease-out',
          maxHeight: '80vh',
          overflowY: 'auto'
        }}
      >
        {/* Handle bar */}
        <div style={{
          width: '40px',
          height: '4px',
          background: 'rgba(255,255,255,0.2)',
          borderRadius: '2px',
          margin: '0 auto 20px'
        }} />

        {/* Title */}
        <h2 style={{ 
          margin: '0 0 4px 0', 
          fontSize: '28px', 
          fontWeight: '700',
          color: 'var(--text-primary)'
        }}>
          Logged.
        </h2>
        
        {/* Current meal name if provided */}
        {currentMealName && (
          <p style={{ 
            margin: '0 0 8px 0', 
            fontSize: '14px', 
            color: 'var(--text-primary)',
            fontWeight: '500'
          }}>
            {currentMealName.length > 60 ? currentMealName.slice(0, 60) + '...' : currentMealName}
          </p>
        )}
        
        {/* MIQ Learning Reinforcement */}
        <p style={{ 
          margin: '0 0 4px 0', 
          fontSize: '13px', 
          color: 'var(--text-secondary)'
        }}>
          MIQ is processing this meal.
        </p>
        
        {/* Secondary reinforcement line */}
        <p style={{ 
          margin: '0 0 12px 0', 
          fontSize: '11px', 
          color: 'rgba(255,255,255,0.4)',
          fontStyle: 'italic'
        }}>
          Eating out still counts — consistency matters more than perfection.
        </p>

        {/* Current Meal Macros - Show what was just logged */}
        {(currentMealCalories > 0 || currentMealProtein > 0) && (
          <div style={{
            background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.15) 0%, rgba(118, 75, 162, 0.1) 100%)',
            borderRadius: '12px',
            padding: '14px 16px',
            marginBottom: '12px',
            border: '1px solid rgba(102, 126, 234, 0.2)'
          }}>
            <p style={{ 
              margin: '0 0 8px 0', 
              fontSize: '11px', 
              color: 'var(--text-secondary)',
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}>
              This meal
            </p>
            <div style={{
              display: 'flex',
              alignItems: 'baseline',
              gap: '16px',
              flexWrap: 'wrap'
            }}>
              <span style={{ 
                fontSize: '24px', 
                fontWeight: '700', 
                color: 'var(--text-primary)' 
              }}>
                {currentMealCalories} kcal
              </span>
              <div style={{
                display: 'flex',
                gap: '12px',
                fontSize: '13px',
                color: 'var(--text-secondary)'
              }}>
                <span>P: <strong style={{ color: 'var(--text-primary)' }}>{currentMealProtein}g</strong></span>
                <span>C: <strong style={{ color: 'var(--text-primary)' }}>{currentMealCarbs}g</strong></span>
                <span>F: <strong style={{ color: 'var(--text-primary)' }}>{currentMealFat}g</strong></span>
              </div>
            </div>
          </div>
        )}

        {/* Medical Sensitivity Alerts - Show when there are health concerns */}
        {medicalAlerts && medicalAlerts.length > 0 && (
          <div style={{
            background: medicalAlerts.some(a => a.severity === 'critical') 
              ? 'rgba(239, 68, 68, 0.12)' 
              : 'rgba(251, 146, 60, 0.1)',
            borderRadius: '12px',
            padding: '14px 16px',
            marginBottom: '12px',
            border: medicalAlerts.some(a => a.severity === 'critical')
              ? '1px solid rgba(239, 68, 68, 0.3)'
              : '1px solid rgba(251, 146, 60, 0.25)'
          }}>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '8px',
              marginBottom: '10px'
            }}>
              <span style={{ fontSize: '16px' }}>
                {medicalAlerts.some(a => a.severity === 'critical') ? '🚫' : '⚠️'}
              </span>
              <span style={{ 
                fontSize: '12px', 
                fontWeight: '600',
                color: medicalAlerts.some(a => a.severity === 'critical') ? '#ef4444' : '#fb923c',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                Health Alert{medicalAlerts.length > 1 ? 's' : ''} ({medicalAlerts.length})
              </span>
            </div>
            
            {/* Show first alert always, rest on expand */}
            {(showAllAlerts ? medicalAlerts : medicalAlerts.slice(0, 2)).map((alert, index) => (
              <div 
                key={index}
                style={{
                  padding: '10px 12px',
                  background: alert.severity === 'critical' 
                    ? 'rgba(239, 68, 68, 0.08)' 
                    : alert.severity === 'warning'
                    ? 'rgba(251, 146, 60, 0.06)'
                    : 'rgba(96, 165, 250, 0.06)',
                  borderRadius: '8px',
                  marginBottom: index < (showAllAlerts ? medicalAlerts.length : Math.min(2, medicalAlerts.length)) - 1 ? '8px' : '0',
                  borderLeft: `3px solid ${
                    alert.severity === 'critical' ? '#ef4444' 
                    : alert.severity === 'warning' ? '#fb923c' 
                    : '#60a5fa'
                  }`
                }}
              >
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'flex-start',
                  gap: '8px'
                }}>
                  <span style={{ fontSize: '14px' }}>{alert.icon}</span>
                  <div style={{ flex: 1 }}>
                    <p style={{ 
                      margin: '0 0 4px 0', 
                      fontSize: '12px', 
                      fontWeight: '600',
                      color: 'var(--text-primary)'
                    }}>
                      {alert.condition}: {alert.trigger} detected
                    </p>
                    <p style={{ 
                      margin: 0, 
                      fontSize: '11px', 
                      color: 'var(--text-secondary)',
                      lineHeight: '1.4'
                    }}>
                      {alert.reason}
                    </p>
                    {/* Smart Swap Suggestion */}
                    {alert.swap_suggestion && (
                      <div style={{
                        marginTop: '8px',
                        padding: '8px 10px',
                        background: 'rgba(34, 197, 94, 0.1)',
                        borderRadius: '6px',
                        border: '1px solid rgba(34, 197, 94, 0.2)'
                      }}>
                        <p style={{ 
                          margin: 0, 
                          fontSize: '11px', 
                          color: '#22c55e',
                          fontWeight: '500',
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: '6px'
                        }}>
                          <span style={{ fontSize: '12px' }}>💡</span>
                          <span>
                            <strong>Try instead:</strong> {alert.swap_suggestion}
                          </span>
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
            
            {/* Show more/less toggle if more than 2 alerts */}
            {medicalAlerts.length > 2 && (
              <button
                onClick={() => setShowAllAlerts(!showAllAlerts)}
                style={{
                  background: 'none',
                  border: 'none',
                  padding: '8px 0 0 0',
                  fontSize: '11px',
                  color: '#667eea',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}
              >
                {showAllAlerts ? '▲ Show less' : `▼ Show ${medicalAlerts.length - 2} more alert${medicalAlerts.length - 2 > 1 ? 's' : ''}`}
              </button>
            )}
            
            {/* Disclaimer */}
            <p style={{ 
              margin: '10px 0 0 0', 
              fontSize: '10px', 
              color: 'var(--text-muted)',
              fontStyle: 'italic',
              borderTop: '1px solid rgba(255,255,255,0.05)',
              paddingTop: '8px'
            }}>
              ⓘ Based on your SmartGoals health profile. Not medical advice.
            </p>
          </div>
        )}

        {/* Dynamic stats - Cumulative totals (Today's progress) */}
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '6px',
          marginBottom: '16px',
          padding: '12px',
          background: 'rgba(255,255,255,0.03)',
          borderRadius: '10px'
        }}>
          <p style={{ 
            margin: '0 0 4px 0', 
            fontSize: '11px', 
            color: 'var(--text-secondary)',
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
          }}>
            Today's total
          </p>
          {/* Calories row */}
          <div style={{ 
            fontSize: '14px', 
            color: 'var(--text-secondary)' 
          }}>
            <strong style={{ color: 'var(--text-primary)' }}>{todayCalories}</strong>/{calorieTarget || '—'} kcal
            {hasAIGoals && (
              <span style={{ 
                fontSize: '11px', 
                color: '#667eea',
                marginLeft: '8px'
              }}>
                🧠 AI
              </span>
            )}
          </div>
          
          {/* Macros row - Always show P/C/F */}
          <div style={{
            display: 'flex',
            gap: '12px',
            fontSize: '12px',
            color: 'var(--text-secondary)'
          }}>
            <span>
              P: <strong style={{ color: 'var(--text-primary)' }}>{todayProtein}g</strong>
            </span>
            <span>
              C: <strong style={{ color: 'var(--text-primary)' }}>{todayCarbs}g</strong>
            </span>
            <span>
              F: <strong style={{ color: 'var(--text-primary)' }}>{todayFat}g</strong>
            </span>
          </div>
          
          {/* Micronutrients toggle - Show if data exists or Pro user */}
          {(micronutrients || isPro) && (
            <button
              onClick={() => setShowMicros(!showMicros)}
              style={{
                background: 'none',
                border: 'none',
                minHeight: '44px',
                padding: '10px 0',
                fontSize: '12px',
                color: '#667eea',
                cursor: 'pointer',
                textAlign: 'left',
                display: 'flex',
                alignItems: 'center',
                gap: '4px'
              }}
            >
              {showMicros ? '▼' : '▶'} View micronutrients
            </button>
          )}
          
          {/* Expanded micronutrients */}
          {showMicros && (micronutrients || isPro) && (
            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '6px',
              padding: '10px',
              background: 'rgba(102, 126, 234, 0.08)',
              borderRadius: '8px',
              fontSize: '11px',
              color: 'var(--text-secondary)'
            }}>
              <span>Fiber: <strong>{micronutrients?.fiber ?? 0}g</strong></span>
              <span>Sugar: <strong>{micronutrients?.sugar ?? 0}g</strong></span>
              <span>Sodium: <strong>{micronutrients?.sodium ?? 0}mg</strong></span>
              <span>Cholesterol: <strong>{micronutrients?.cholesterol ?? 0}mg</strong></span>
            </div>
          )}
        </div>

        {/* Edge case: First meal of the day */}
        {mealsLoggedToday <= 1 && (
          <div style={{
            padding: '12px',
            background: 'rgba(102, 126, 234, 0.1)',
            borderRadius: '10px',
            marginBottom: '16px',
            border: '1px solid rgba(102, 126, 234, 0.2)'
          }}>
            <p style={{ 
              margin: 0, 
              fontSize: '13px', 
              color: 'var(--text-secondary)',
              lineHeight: '1.4'
            }}>
              Great start. Log 1–2 more meals to unlock timing insights.
            </p>
          </div>
        )}

        {/* Evening snack suggestion (edge case: 100-500 kcal remaining 6-9pm) */}
        {hasEveningGap && eveningSnack && (
          <div style={{
            padding: '12px',
            background: 'rgba(45, 212, 191, 0.08)',
            borderRadius: '10px',
            marginBottom: '16px',
            border: '1px solid rgba(45, 212, 191, 0.2)'
          }}>
            <p style={{ 
              margin: 0, 
              fontSize: '12px', 
              color: 'var(--text-secondary)',
              lineHeight: '1.4'
            }}>
              🌙 If you want something small: <strong style={{ color: 'var(--text-primary)' }}>{eveningSnack.name}</strong> ({eveningSnack.calories})
            </p>
          </div>
        )}

        {/* Blurred Premium Insights — tease AI value for non-premium users */}
        {!isPro && (
          <div
            data-testid="blurred-premium-insights"
            style={{
              position: 'relative',
              marginBottom: '16px',
              borderRadius: '12px',
              overflow: 'hidden',
              border: '1px solid rgba(147, 51, 234, 0.25)'
            }}
          >
            {/* Blurred content underneath */}
            <div style={{
              padding: '14px 16px',
              background: 'linear-gradient(135deg, rgba(147, 51, 234, 0.06) 0%, rgba(102, 126, 234, 0.04) 100%)',
              filter: 'blur(4px)',
              WebkitFilter: 'blur(4px)',
              userSelect: 'none',
              pointerEvents: 'none'
            }}>
              <div style={{ fontSize: '11px', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '10px' }}>
                AI Meal Insights
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ fontSize: '14px' }}>&#x23F0;</span>
                  <div>
                    <div style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-primary)' }}>Optimal next meal: 3.5 hrs</div>
                    <div style={{ fontSize: '10px', color: 'var(--text-secondary)' }}>Based on glycemic load of this meal</div>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ fontSize: '14px' }}>&#x2696;</span>
                  <div>
                    <div style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-primary)' }}>Macro balance: 72% optimal</div>
                    <div style={{ fontSize: '10px', color: 'var(--text-secondary)' }}>Protein-to-carb ratio analysis</div>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ fontSize: '14px' }}>&#x1F4CA;</span>
                  <div>
                    <div style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-primary)' }}>Satiety score: High</div>
                    <div style={{ fontSize: '10px', color: 'var(--text-secondary)' }}>Fiber + protein combination effect</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Lock overlay */}
            <div
              data-testid="premium-insights-lock-overlay"
              onClick={onUpgrade}
              style={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'rgba(10, 10, 26, 0.55)',
                backdropFilter: 'blur(1px)',
                cursor: 'pointer',
                gap: '8px'
              }}
            >
              <div style={{
                width: '36px',
                height: '36px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, rgba(147, 51, 234, 0.3) 0%, rgba(102, 126, 234, 0.2) 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '1px solid rgba(147, 51, 234, 0.4)',
                fontSize: '16px'
              }}>
                &#x1F512;
              </div>
              <span style={{
                fontSize: '13px',
                fontWeight: '600',
                color: 'white'
              }}>
                Unlock AI Meal Insights
              </span>
              <span style={{
                fontSize: '11px',
                color: 'rgba(255,255,255,0.7)'
              }}>
                {isTrial ? 'Upgrade to keep these after your trial' : 'Available with Genius Plaite'}
              </span>
              <span style={{
                fontSize: '9px',
                color: 'rgba(255,255,255,0.45)',
                marginTop: '2px'
              }}>
                3,214 premium insights delivered this week
              </span>
            </div>
          </div>
        )}

        {/* What's next? */}
        <h3 style={{ 
          margin: '0 0 16px 0', 
          fontSize: '16px', 
          fontWeight: '600',
          color: 'var(--text-primary)'
        }}>
          What&apos;s next?
        </h3>

        {/* Primary CTA */}
        <button
          data-testid="post-meal-primary-cta"
          onClick={handlePrimaryClick}
          style={{
            width: '100%',
            minHeight: '52px',
            padding: '16px',
            fontSize: '15px',
            fontWeight: '600',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            border: 'none',
            borderRadius: '12px',
            cursor: 'pointer',
            marginBottom: '12px',
            transition: 'transform 0.15s ease, opacity 0.15s ease',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          {primaryCTA.label}
        </button>

        {/* Secondary Actions - Premium Features (Unlocked) */}
        {(showSmartSwaps || showBarcode) && (
          <div style={{ marginBottom: '16px' }}>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center', 
              gap: '6px',
              marginBottom: '8px'
            }}>
              <p style={{ 
                margin: 0, 
                fontSize: '10px', 
                color: 'rgba(147, 51, 234, 0.7)'
              }}>
                🟣 Premium features — included with free trial
              </p>
              <span 
                data-testid="post-meal-premium-info-tooltip"
                title="These are advanced MetaBolicIQ (MIQ) 🧠 features. You currently have full access during your free trial."
                style={{ 
                  fontSize: '11px', 
                  color: 'rgba(147, 51, 234, 0.7)',
                  cursor: 'help',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '20px',
                  height: '20px',
                  borderRadius: '50%',
                  background: 'rgba(147, 51, 234, 0.1)',
                  border: '1px solid rgba(147, 51, 234, 0.2)'
                }}>ℹ</span>
            </div>
            <div style={{ 
              display: 'flex', 
              gap: '10px'
            }}>
              {showSmartSwaps && (
                <button
                  data-testid="post-meal-smart-swaps"
                  onClick={handleSmartSwaps}
                  style={{
                    flex: 1,
                    minHeight: '48px',
                    padding: '12px',
                    fontSize: '13px',
                    fontWeight: '500',
                    background: 'rgba(147, 51, 234, 0.1)',
                    color: '#9333ea',
                    border: '1px solid rgba(147, 51, 234, 0.3)',
                    borderRadius: '10px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  🔄 Smart Swaps
                </button>
              )}
              {showBarcode && (
                <button
                  data-testid="post-meal-barcode"
                  onClick={handleBarcode}
                  style={{
                    flex: 1,
                    minHeight: '48px',
                    padding: '12px',
                    fontSize: '13px',
                    fontWeight: '500',
                    background: 'rgba(147, 51, 234, 0.1)',
                    color: '#9333ea',
                    border: '1px solid rgba(147, 51, 234, 0.3)',
                    borderRadius: '10px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  🔍 Barcode Scanner
                </button>
              )}
            </div>
          </div>
        )}

        {/* Not now */}
        <button
          data-testid="post-meal-not-now"
          onClick={handleNotNow}
          style={{
            width: '100%',
            minHeight: '48px',
            padding: '14px',
            fontSize: '14px',
            fontWeight: '500',
            background: 'transparent',
            color: 'var(--text-secondary)',
            border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: '10px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          Not now
        </button>

        {/* CSS Animations */}
        <style>{`
          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          @keyframes slideUp {
            from { transform: translateY(100%); }
            to { transform: translateY(0); }
          }
        `}</style>
      </div>
    </>
  );
};

export default PostMealCompleteSheet;
